"use client";

import {
  AlertTriangle,
  ChevronRight,
  Plus,
  Server,
  ShieldCheck,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Page } from "@/types/common";

type OverviewClient = { id: string; name: string; status?: string };
type OverviewInstance = unknown;
type MetricCardProps = { icon: LucideIcon; label: string; value: string };
type ChartRowProps = { icon: LucideIcon; label: string; value: number; max: number };

function MetricCard({ icon: Icon, label, value }: MetricCardProps) {
  return (
    <Card>
      <CardContent className="flex min-h-32 flex-col justify-between gap-5 p-5">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground"><Icon className="text-primary" />{label}</div>
        <div className="text-4xl font-semibold tracking-tight">{value}</div>
      </CardContent>
    </Card>
  );
}

function ChartRow({ icon: Icon, label, value, max }: ChartRowProps) {
  const progress = max > 0 ? Math.round((value / max) * 100) : 0;
  return <div className="flex items-center gap-3"><Icon className="shrink-0 text-muted-foreground" /><div className="min-w-0 flex-1"><div className="mb-1 flex items-center justify-between gap-3 text-sm"><span>{label}</span><span className="font-medium">{value}</span></div><Progress value={progress} aria-label={`${label}: ${value}`} /></div></div>;
}

export function OverviewPage({ clients, instances, stats, selectedClient, onSelectClient, onDeploy, onNavigate, isLoading }: { clients: OverviewClient[]; instances: OverviewInstance[]; stats: { active: number; warning: number; blocked: number; totalSpend: number; online: number; tokens: number }; selectedClient: OverviewClient | null; onSelectClient: (id: string) => void; onDeploy: (id?: string) => void; onNavigate: (page: Page) => void; isLoading: boolean }) {
  const attentionCount = stats.warning + stats.blocked;
  const chartMax = Math.max(stats.online, instances.length, clients.length, attentionCount, 1);

  return (
    <div className="flex flex-col gap-4 pb-6" aria-busy={isLoading}>
      <Tabs defaultValue="overview" className="gap-4">
        <div className="flex items-center justify-between gap-3 border-b pb-2"><TabsList className="h-auto justify-start gap-1 bg-transparent p-0"><TabsTrigger value="overview" className="rounded-md px-3 py-2 data-active:bg-primary/10 data-active:text-primary">Обзор</TabsTrigger></TabsList><Button size="sm" onClick={() => onDeploy(selectedClient?.id)}><Plus data-icon="inline-start" /> Новая установка</Button></div>

        <TabsContent value="overview" className="mt-0 flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"><MetricCard icon={Server} label="Онлайн Mini" value={stats.online.toString()} /><MetricCard icon={Users} label="Клиенты" value={clients.length.toString()} /><MetricCard icon={ShieldCheck} label="Все Mini" value={instances.length.toString()} /><MetricCard icon={AlertTriangle} label="Требуют внимания" value={attentionCount.toString()} /></div>

          <Card>
            <CardHeader><CardTitle className="text-base">Сводка PanelAI</CardTitle><p className="mt-1 text-sm text-muted-foreground">Текущие значения из загруженных данных</p></CardHeader>
            <CardContent className="flex flex-col gap-4"><ChartRow icon={Server} label="Онлайн Mini" value={stats.online} max={chartMax} /><ChartRow icon={ShieldCheck} label="Все Mini" value={instances.length} max={chartMax} /><ChartRow icon={Users} label="Клиенты" value={clients.length} max={chartMax} /><ChartRow icon={AlertTriangle} label="Требуют внимания" value={attentionCount} max={chartMax} /></CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between"><div><CardTitle className="text-base">Клиенты</CardTitle><p className="mt-1 text-sm text-muted-foreground">Выберите клиента, чтобы открыть его установки</p></div><Button variant="ghost" size="sm" onClick={() => onNavigate("clients")}>Все клиенты <ChevronRight data-icon="inline-end" /></Button></CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{clients.slice(0, 6).map((client) => <button type="button" key={client.id} className="flex items-center justify-between rounded-lg border p-4 text-left transition-colors hover:bg-muted/40" onClick={() => onSelectClient(client.id)}><span><span className="block font-medium">{client.name}</span><span className="text-sm text-muted-foreground">{client.status === "active" ? "Активен" : client.status || "Статус не указан"}</span></span><ChevronRight /></button>)}{clients.length === 0 ? <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground md:col-span-2 xl:col-span-3">Клиенты ещё не добавлены.</div> : null}</CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
